import { Checkbox, Input, Table } from 'antd';
import { ColumnType } from 'antd/lib/table';
import { DefectiveReturnItemPayload, SparingRequestPayload } from 'models/SparingRequests';
import { useState } from 'react';

type DefectiveReturnRow = {
  lineNumber: number;
  productNumber: string;
  serialNumber: string;
  caseNumber: string;
  rmaNumber: string;
  quantity: number;
  used: boolean;
  notes?: string;
};

type Props = {
  items: DefectiveReturnItemPayload[];
  parentValues?: SparingRequestPayload;
  isEdit?: boolean;
  onItemChange?: (updatedItems: DefectiveReturnItemPayload[]) => void;
};

export const DefectiveReturnsTableReadOnly = ({ items, parentValues, isEdit = false, onItemChange }: Props): JSX.Element => {
  const [localItems, setLocalItems] = useState<DefectiveReturnItemPayload[]>(items);

  const handleItemChange = (index: number, field: keyof DefectiveReturnItemPayload, value: string | boolean): void => {
    const updatedItems = [...localItems];

    updatedItems[index] = { ...updatedItems[index], [field]: value };
    setLocalItems(updatedItems);
    onItemChange?.(updatedItems);
  };

  const cols: ColumnType<DefectiveReturnItemPayload>[] = [
    {
      title: `Didn't Use`,
      render: (_value, _record, index): JSX.Element => {
        const isUsed = localItems[index]?.used ?? true;

        return (
          <Checkbox
            checked={!isUsed}
            onChange={(e): void => {
              handleItemChange(index, 'used', !e.target.checked);
            }}
          />
        );
      },
      align: 'center',
      width: '12ch'
    },
    {
      title: 'Return Part Number',
      dataIndex: 'productNumber',
      ellipsis: true,
      width: '25ch'
    },
    {
      title: 'Return Serial Number',
      dataIndex: 'serialNumber',
      render: (value: string, _record, index): JSX.Element => (
        <Input
          value={value}
          onChange={(e): void => {
            handleItemChange(index, 'serialNumber', e.target.value);
          }}
        />
      )
    },
    { title: 'Qty', dataIndex: 'quantity', width: '9ch', align: 'center' },
    {
      title: 'OEM Case Number',
      dataIndex: 'caseNumber',
      render: (value: string, _record, index): JSX.Element => (
        <Input
          value={value}
          onChange={(e): void => {
            handleItemChange(index, 'caseNumber', e.target.value);
          }}
        />
      )
    },
    {
      title: 'OEM RMA Number',
      dataIndex: 'rmaNumber',
      render: (value: string, _record, index): JSX.Element => (
        <Input
          value={value}
          onChange={(e): void => {
            handleItemChange(index, 'rmaNumber', e.target.value);
          }}
        />
      )
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      render: (value: string, _record, index): JSX.Element => (
        <Input.TextArea
          rows={2}
          value={value}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>): void => {
            handleItemChange(index, 'notes', e.target.value);
          }}
        />
      )
    }
  ];

  return <Table className="narrow-table" rowKey={(_, index): string => String(index)} dataSource={localItems} columns={cols} pagination={false} />;
};
